import { useMemo, useState, type ReactNode } from 'react';
import { Box, Text, VStack } from '@chakra-ui/react';
import { RiftboundMenuItemButton } from './ui/menu-item-button';
import { RiftboundMenuTriggerButton, type RiftboundMenuTriggerButtonProps } from './ui/menu-trigger-button';
import { RiftboundPopover, type RiftboundPopoverPlacement } from './ui/popover';

export type RiftboundSelectMenuOption<TValue extends string = string> = {
  value: TValue;
  label: ReactNode;
  triggerLabel?: string;
  section?: string;
  disabled?: boolean;
};

export type RiftboundSelectMenuProps<TValue extends string = string> = {
  value: TValue;
  options: readonly RiftboundSelectMenuOption<TValue>[];
  onValueChange: (value: TValue) => void;
  ariaLabel: string;
  placeholder?: string;
  placement?: RiftboundPopoverPlacement;
  menuWidth?: string | number;
  menuMaxH?: string | number;
  portal?: boolean;
  disabled?: boolean;
  triggerFullWidth?: boolean;
  triggerProps?: Omit<
    RiftboundMenuTriggerButtonProps,
    'active' | 'aria-label' | 'chevronExpanded' | 'label' | 'onClick'
  >;
};

export function RiftboundSelectMenu<TValue extends string = string>({
  value,
  options,
  onValueChange,
  ariaLabel,
  placeholder = 'Select',
  placement = 'bottom-end',
  menuWidth = '220px',
  menuMaxH = '320px',
  portal = true,
  disabled,
  triggerFullWidth = true,
  triggerProps,
}: RiftboundSelectMenuProps<TValue>) {
  const [open, setOpen] = useState(false);
  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );
  const selectedLabel = selectedOption?.triggerLabel
    ?? (typeof selectedOption?.label === 'string' ? selectedOption.label : placeholder);

  return (
    <RiftboundPopover
      open={open}
      onOpenChange={setOpen}
      placement={placement}
      width={menuWidth}
      bodyPadding={1.5}
      variant="panel"
      portal={portal}
      trigger={
        <RiftboundMenuTriggerButton
          aria-label={ariaLabel}
          label={selectedLabel}
          active={open}
          chevronExpanded={open}
          chevronAlign="end"
          disabled={disabled}
          w={triggerFullWidth ? '100%' : undefined}
          {...triggerProps}
        />
      }
    >
      <VStack align="stretch" gap={1} maxH={menuMaxH} overflowY="auto">
        {options.map((option, index) => {
          const selected = option.value === value;
          const previousSection = index > 0 ? options[index - 1]?.section : undefined;
          const showSection = option.section && option.section !== previousSection;

          return (
            <Box key={option.value}>
              {showSection ? (
                <Text
                  as="div"
                  px={3}
                  pt={index === 0 ? 1 : 3}
                  pb={1}
                  fontSize="2xs"
                  fontWeight="bold"
                  letterSpacing="0.08em"
                  textTransform="uppercase"
                  color="textMuted"
                >
                  {option.section}
                </Text>
              ) : null}
              <RiftboundMenuItemButton
                selected={selected}
                disabled={option.disabled}
                onClick={() => {
                  onValueChange(option.value);
                  setOpen(false);
                }}
              >
                {option.label}
              </RiftboundMenuItemButton>
            </Box>
          );
        })}
      </VStack>
    </RiftboundPopover>
  );
}
